import { useCallback, useEffect, useRef } from 'react';
import { useHistory, useLocation } from 'quartz-routing/react-router';
import FormattedMessage from 'I18n/components/FormattedMessage';
import RouterButton from 'ui-addon-react-router-dom/RouterButton';
import RouterLink from 'ui-addon-react-router-dom/RouterLink';
import styled from 'foundations-theming/utils';
import { removeTrailingSlash } from '../routing/RoutingUtils';
import { startConfetti } from 'academy-common-lib/utils/confetti';
import { jsx as _jsx } from "react/jsx-runtime";
const LikeLink = styled.span.withConfig({
  displayName: "TrackViewAwardLink__LikeLink"
})(["color:", ";cursor:pointer;font-weight:500;transition:all 0.15s ease-out;-webkit-font-smoothing:antialiased;-moz-osx-font-smoothing:grayscale;&:hover{text-decoration:underline;color:", ";}"], ({
  theme,
  color
}) => color || theme.color['text-interactive-default'], ({
  theme,
  hoverColor
}) => hoverColor || theme.color['text-interactive-default-hover']);
const AwardLink = styled(LikeLink).withConfig({
  displayName: "TrackViewAwardLink__AwardLink"
})(["color:", ";text-decoration:underline;:hover{color:", ";}"], ({
  theme,
  use
}) => use === 'on-light' ? theme.color['text-interactive-default'] : theme.color['text-interactive-on-fill'], ({
  theme,
  use
}) => use === 'on-light' ? theme.color['text-interactive-default-hover'] : theme.color['text-interactive-on-fill-hover']);
const TrackViewAwardLink = ({
  award,
  certification,
  trackId,
  useCurrentText,
  useButton = true,
  use = useButton ? 'primary' : 'dark',
  expired,
  likeLink,
  confettiAwardedAtMs,
  confettiWindowMs = 600000
}) => {
  const history = useHistory();
  const {
    pathname,
    search
  } = useLocation();
  const confettiCleanupRef = useRef(undefined);
  const maybeTriggerConfetti = useCallback(() => {
    if (!confettiAwardedAtMs) {
      return;
    }
    if (Date.now() - confettiAwardedAtMs < confettiWindowMs) {
      var _confettiCleanupRef$c;
      (_confettiCleanupRef$c = confettiCleanupRef.current) === null || _confettiCleanupRef$c === void 0 || _confettiCleanupRef$c.call(confettiCleanupRef);
      confettiCleanupRef.current = startConfetti();
    }
  }, [confettiAwardedAtMs, confettiWindowMs]);
  useEffect(() => {
    return () => {
      var _confettiCleanupRef$c2;
      (_confettiCleanupRef$c2 = confettiCleanupRef.current) === null || _confettiCleanupRef$c2 === void 0 || _confettiCleanupRef$c2.call(confettiCleanupRef);
      confettiCleanupRef.current = undefined;
    };
  }, []);
  if (!award) {
    return null;
  }
  const noTrailingUrl = pathname === '/' ? '' : removeTrailingSlash(pathname);
  const link = `${noTrailingUrl}/award/${trackId}${search}`;
  let label = /*#__PURE__*/_jsx(FormattedMessage, {
    message: "academyCommon.common.showCertificateOfCompletion"
  });
  if (certification) {
    label = useCurrentText ? /*#__PURE__*/_jsx(FormattedMessage, {
      message: "academyCommon.common.showCurrentCertification"
    }) : /*#__PURE__*/_jsx(FormattedMessage, {
      message: "academyCommon.common.showCertification"
    });
  }
  if (expired) {
    label = /*#__PURE__*/_jsx(FormattedMessage, {
      message: "academyCommon.common.showExpiredCertification"
    });
  }
  if (likeLink) {
    return (
      /*#__PURE__*/
      // @bend-expect trellis-tokenized
      _jsx(AwardLink, {
        onClick: e => {
          e.preventDefault();
          maybeTriggerConfetti();
          history.push(link);
        },
        use: use,
        children: label
      })
    );
  }
  if (useButton) {
    return /*#__PURE__*/_jsx(RouterButton, {
      "data-test-id": "view-award-button",
      onClick: maybeTriggerConfetti,
      to: link,
      use: use,
      children: label
    });
  }
  return /*#__PURE__*/_jsx(RouterLink, {
    "data-test-id": "view-award-link",
    onClick: maybeTriggerConfetti,
    to: link,
    use: use,
    children: label
  });
};
export default TrackViewAwardLink;